import React, { useState, useEffect } from 'react';
import { TextField, IconButton, Tooltip, makeStyles } from '@material-ui/core';
import {
  Create as EditIcon,
  CompareArrowsRounded,
  Visibility as ViewIcon,
} from '@material-ui/icons';
import fontFamily from '../../../assets/css/fonts';
import color from '../../../assets/css/colors';
import QueryParam from '../../../services/QueryParamService';
import { getSystemDate } from '../../../services/ProfileService';
import {
  listExerciseDeclaration,
  createExerciseDeclaration,
  updateExerciseDeclaration,
  createExerciseNotice,
  updateExerciseNotice,
} from '../../../services/ExerciseDeclarationService';
import Table, {
  columnType,
  tempFilteredRows,
} from '../../../components/ClearingTable/Table';
import AutoCompleteMasterAccountNo from '../../../components/AutoComplete/AutoCompleteMasterAccountNo';
import AutoCompleteBranch from '../../../components/AutoComplete/AutoCompleteBranch';
import AutoCompleteRep from '../../../components/AutoComplete/AutoCompleteRep';
import Button from '../../../components/Buttons/Button';
import Text from '../../../components/Typography/Text';
import SelectSystemCode from '../../../components/Dropdown/SelectSystemCode';
import {
  notifyInfo,
  notifyError,
  notifySuccess,
  notifyWarning,
} from '../../../components/Messages/Notification';
import {
  CustomBodyRenderAlign,
  CustomBodyRenderQty,
} from '../../../components/Table/CustomBodyRender';
import AutoCompleteAccountName from '../../../components/AutoComplete/AutoCompleteAccountName';
import ExerciseDeclarationModal from './ExerciseDeclarationModal';
import ExerciseNoticeModal from './ExerciseNoticeModal';
import ExerciseDeclarationDetails from './ExerciseDeclarationDetails';
import ExerciseNoticeDetails from './ExerciseNoticeDetails';
import { useConfirm } from 'material-ui-confirm';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 800,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: 25,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderBottom: '1px solid #ccc',
    display: 'flex',
    '& div:nth-child(1)': {
      flex: 1,
    },
    '& div:nth-child(2)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {
          fill: color.primary.main,
        },
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    fontFamily: fontFamily.headers,
    '& p': {
      fontSize: 16,
      lineHeight: '24px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 'auto',
    },
  },
  closeButton: {
    '& button': {
      marginTop: '-4px !important',
    },
  },
}));

export default function ExerciseDeclaration() {
  const classes = useStyles();
  const [openNotice, setOpenNotice] = React.useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const [openNoticeDetails, setOpenNoticeDetails] = React.useState(false);
  const [openDeclarationDetails, setOpenDeclarationDetails] = React.useState(false);
  const [rowData, setRowData] = React.useState({});
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState({
    search: false,
    save: false,
    delete: false,
  });
  const [rows, setRows] = useState([]);
  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      systemDate: '',
      accountName: '',
      masterAccountNo: '',
      branch: '',
      rep: '',
      broker: '',
    })
  );
  const [pagination, setPagination] = React.useState({});
  const [searchDataCsv, setSearchDataCsv] = React.useState([]);
  const [previousRows, setPreviousRows] = useState(0);
  const [lockTable, setLockTable] = useState(true);
  const confirm = useConfirm();

  useEffect(
    () => {
      async function init() {
        const systemDate = await getSystemDate();
        setCurrentDate(systemDate);

        if (searchData.systemDate) return;

        setSearchData({
          ...searchData,
          systemDate: systemDate,
        });
      }

      init();
    },
    // eslint-disable-next-line
    []
  );

  const columns = [
    {
      name: 'actions',
      label: 'Actions',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <div align={'left'} className="grd-row" style={{ margin: 0, padding: 0 }}>
              {tempFilteredRows[dataIndex]?.expirationDate === currentDate && (
                <div align={'left'} className="grd-cell">
                  <Tooltip title="Exercise Declaration" arrow>
                    <IconButton
                      aria-label="edit"
                      disabled={loading.delete}
                      onClick={() => handleOpen(tempFilteredRows[dataIndex])}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              {tempFilteredRows[dataIndex]?.expirationDate === currentDate && (
                <div align={'left'} className="grd-cell">
                  <IconButton
                    aria-label="details"
                    onClick={() => {
                      setRowData(tempFilteredRows[dataIndex]);
                      setOpenDeclarationDetails(true);
                    }}
                  >
                    <ViewIcon />
                  </IconButton>
                </div>
              )}
              {currentDate < tempFilteredRows[dataIndex]?.expirationDate && (
                <div align={'left'} className="grd-cell">
                  <Tooltip title="Exercise Notice" arrow>
                    <IconButton
                      onClick={() => {
                        setRowData(tempFilteredRows[dataIndex]);
                        setOpenNotice(true);
                      }}
                    >
                      <CompareArrowsRounded />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              {currentDate < tempFilteredRows[dataIndex]?.expirationDate && (
                <div align={'left'} className="grd-cell">
                  <IconButton
                    aria-label="details"
                    onClick={() => {
                      setRowData(tempFilteredRows[dataIndex]);
                      setOpenNoticeDetails(true);
                    }}
                  >
                    <ViewIcon />
                  </IconButton>
                </div>
              )}
            </div>
          );
        },
        sort: false,
      },
    },
    {
      name: 'contraryId',
      label: 'ID',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'accountId',
      label: 'Account ID',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountName',
      label: 'Account Name',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'rep',
      label: 'Rep',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'branch',
      label: 'Branch',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'marginType',
      label: 'Margin Type',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'symbol',
      label: 'Symbol',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'symbolDescription',
      label: 'Symbol Description',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'originalCusip',
      label: 'Original Cusip',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'tdQty',
      label: 'TD Qty',
      type: columnType.quantity,
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
    },
    {
      name: 'positionQty',
      label: 'Position Qty',
      type: columnType.quantity,
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
    },
    {
      name: 'exerciseQty',
      label: 'Exercise Qty',
      type: columnType.quantity,
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
    },
    {
      name: 'eed',
      label: 'EED',
      type: columnType.quantity,
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
    },
    {
      name: 'dned',
      label: 'DNED',
      type: columnType.quantity,
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
    },
    {
      name: 'systemDate',
      label: 'System Date',
      type: columnType.date,
    },
    {
      name: 'expirationDate',
      label: 'Expiration Date',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    selectableRows: 'none',
    serverSide: true,
  };

  const handleCloseDetails = () => {
    setOpenDeclarationDetails(false);
    setOpenNoticeDetails(false);
  };

  const handleChange = (e, checkboxValue) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({
      ...searchData,
      [input.name]:
        checkboxValue === true || checkboxValue === false ? checkboxValue : input.value,
    });
  };

  const handleOpen = (data) => {
    if (!data.dned || !data.eed) {
      //ADD default values
      data = {
        ...data,
        accountId: data.accountId || 0,
        subAccountNo: data.subAccountNo || '',
        symbol: data.symbol || '',
        originalCusip: data.originalCusip || '',
        eed: data.eed || 0,
        dned: data.dned || 0,
        systemDate: data.systemDate || '',
        status: data.status || '',
      };
    }

    setRowData(data);
    setOpen(true);
  };

  const handleClose = async (data, isEdit) => {
    if (!data) {
      setOpen(false);
      return;
    }
    try {
      setLoading({ ...loading, save: true });

      if (isEdit) {
        //EDIT
        const resp = await updateExerciseDeclaration(data);
        const rowsCopy = [...rows];
        const index = rows.indexOf(rowData);
        rowsCopy[index] = resp.exerciseDeclaration;
        setRows(rowsCopy);
        notifySuccess('Exercise Declaration has been updated.');
        handleSearch();
      } else {
        //ADD
        const resp = await createExerciseDeclaration(data);
        setRows([resp.exerciseDeclaration, ...rows]);
        notifySuccess('New Exercise Declaration has been added.');
        handleSearch();
      }
      setOpen(false);
    } catch (error) {
      console.error(error);
      setLoading({ ...loading, save: false });
      notifyError(error.message);
    }

    setLoading({ ...loading, save: false });
  };

  const handleCloseNotice = async (data, isEdit) => {
    if (!data) {
      setOpenNotice(false);
      return;
    }
    try {
      setLoading({ ...loading, save: true });

      if (isEdit) {
        //EDIT
        const resp = await updateExerciseNotice(data);
        const rowsCopy = [...rows];
        const index = rows.indexOf(rowData);
        rowsCopy[index] = resp.exerciseDeclaration;
        setRows(rowsCopy);
        notifySuccess('Exercise Notice has been updated.');
        handleSearch();
      } else {
        //ADD
        const resp = await createExerciseNotice(data);
        setRows([resp.exerciseDeclaration, ...rows]);
        notifySuccess('New Exercise Notice has been added.');
        handleSearch();
      }
      setOpenNotice(false);
    } catch (error) {
      console.error(error);
      setLoading({ ...loading, save: false });
      notifyError(error.message);
    }

    setLoading({ ...loading, save: false });
  };

  const getCsvData = async () => {
    if (lockTable === true) {
      return [];
    }
    const MAX_RECORDS_ALLOWED = 100000;

    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: MAX_RECORDS_ALLOWED,
    };

    const data = await listExerciseDeclaration(searchDataCsv, paginationCopy);

    if (data.exerciseDeclarationsList.length >= MAX_RECORDS_ALLOWED) {
      confirm({
        description:
          'Only ' +
          MAX_RECORDS_ALLOWED.toLocaleString() +
          ' items will be downloaded, if you need more please use the metabase.',
        confirmationText: 'I understand',
      });
    }

    return data.exerciseDeclarationsList;
  };

  const handleSearch = async (paginationChange) => {
    if (!searchData.systemDate) {
      notifyWarning('System Date is required.');
      return;
    }
    try {
      setLoading({ ...loading, search: true });
      if (paginationChange !== true) {
        setLockTable(false);
      }
      if (paginationChange === true) {
        if (lockTable === true) {
          return;
        }
      }
      QueryParam.set(searchData);

      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      if (previousRows !== 0) {
        if (previousRows < pagination.rowsPerPage * pagination.pageNo) {
          paginationCopy.pageNo = 0;
        }
      }
      if (paginationChange !== true) {
        paginationCopy.filterNames = [];
        paginationCopy.filterValues = [];
      }

      const data = await listExerciseDeclaration(searchData, paginationCopy);

      setSearchDataCsv(searchData);
      setPreviousRows(data.summary.totalRows);
      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);
      setRows(data.exerciseDeclarationsList);

      if (paginationChange !== true) {
        notifyInfo(
          data.summary.totalRows > 0
            ? data.summary.totalRows + ' search result(s)'
            : 'No records found.'
        );
      }
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    } finally {
      setLoading({ ...loading, search: false });
    }
  };

  /*=========================================================================================
  Table component
  ===========================================================================================*/
  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Exercise Declaration" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading.search}
              loading={loading.search}
              type="search"
              label={loading.search ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <TextField
              name="systemDate"
              fullWidth
              label="System Date"
              type="date"
              value={searchData.systemDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required={true}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountName
              freeSolo={true}
              required={false}
              label="Account Name"
              name="accountName"
              value={searchData.accountName}
              onChange={handleChange}
              onKeyDown={(e) => (e.keyCode === 13 ? handleSearch() : null)}
            ></AutoCompleteAccountName>
          </div>
          <div className="grd-cell">
            <AutoCompleteMasterAccountNo
              freeSolo={true}
              required={false}
              name="masterAccountNo"
              label="Master Account No"
              value={searchData.masterAccountNo}
              onChange={handleChange}
              type="" //client or gl
              onKeyDown={(e) => (e.keyCode === 13 ? handleSearch() : null)}
            ></AutoCompleteMasterAccountNo>
          </div>
          <div className="grd-cell">
            <AutoCompleteBranch
              required={false}
              freeSolo={true}
              name="branch"
              label="Branch"
              value={searchData.branch}
              onChange={handleChange}
              type="text"
              onKeyDown={(e) => (e.keyCode === 13 ? handleSearch() : null)}
            ></AutoCompleteBranch>
          </div>
          <div className="grd-cell">
            <AutoCompleteRep
              required={false}
              freeSolo={true}
              name="rep"
              label="Rep"
              value={searchData.rep}
              onChange={handleChange}
              onKeyDown={(e) => (e.keyCode === 13 ? handleSearch() : null)}
            ></AutoCompleteRep>
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="broker"
              label="Broker"
              type="Broker"
              value={searchData.broker}
              onChange={handleChange}
            ></SelectSystemCode>
          </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Exercise Declaration'}
          data={rows}
          columns={columns}
          options={options}
          onSearch={handleSearch}
          pagination={pagination}
          setPagination={setPagination}
          getCsvData={getCsvData}
        />
      </div>
      {open && (
        <ExerciseDeclarationModal
          onClose={handleClose}
          open={open}
          value={rowData}
          loading={loading.save}
        ></ExerciseDeclarationModal>
      )}
      {openNotice && (
        <ExerciseNoticeModal
          onClose={handleCloseNotice}
          open={openNotice}
          value={rowData}
          loading={loading.save}
        ></ExerciseNoticeModal>
      )}
      {openDeclarationDetails && (
        <ExerciseDeclarationDetails
          onClose={handleCloseDetails}
          open={openDeclarationDetails}
          value={rowData}
        ></ExerciseDeclarationDetails>
      )}
      {openNoticeDetails && (
        <ExerciseNoticeDetails
          onClose={handleCloseDetails}
          open={openNoticeDetails}
          value={rowData}
        ></ExerciseNoticeDetails>
      )}
    </form>
  );
}
